import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, History, PackagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { StockOverview } from "@/components/StockOverview";
import { StockAdminTools } from "@/components/StockAdminTools";
import { RollStockSummary } from "@/components/RollStockSummary";
import { StudioRolls } from "@/components/StudioRolls";
import { toast } from "sonner";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { Kpi, PageHead, EmptyState } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const EMPTY = { catalog_sku_id: "", quantity: "", unit_cost: "", low_threshold: "" };

export default function Stock() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [restockFor, setRestockFor] = useState(null);
  const [restock, setRestock] = useState({ quantity: "", unit_cost: "" });
  const [history, setHistory] = useState(null);
  const [edit, setEdit] = useState(null);
  const [eform, setEform] = useState({ quantity: "", unit_cost: "", low_threshold: "" });
  const [del, setDel] = useState(null);
  const isAdmin = user.role === "network_admin";
  const isAcct = user.role === "accountant";
  const canManageStock = isAdmin || isAcct;
  const canWrite = can(user, "stock:write");
  const adminNeedsStudio = isAdmin && studioId === "all";

  const load = useCallback(() => {
    const params = isAdmin && studioId !== "all" ? { studio_id: studioId } : {};
    api.get("/stock", { params }).then(({ data }) => setItems(data));
  }, [isAdmin, studioId]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/studios").then(({ data }) => setStudios(data));
  }, [isAdmin]);
  useEffect(() => {
    if (canWrite) api.get("/catalog").then(({ data }) => setCatalog(data));
  }, [canWrite]);

  const submit = async () => {
    try {
      await api.post("/stock", {
        catalog_sku_id: form.catalog_sku_id,
        quantity: Number(form.quantity || 0),
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
        low_threshold: Number(form.low_threshold || 0),
        ...(isAdmin ? { studio_id: studioId === "all" ? null : studioId } : {}),
      });
      toast.success("Stock item added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openEdit = (item) => {
    setEform({
      quantity: String(item.quantity),
      unit_cost: String(item.unit_cost),
      low_threshold: String(item.low_threshold ?? 0),
    });
    setEdit(item);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/stock/${edit.id}`, {
        quantity: Number(eform.quantity || 0),
        unit_cost: Number(eform.unit_cost || 0),
        low_threshold: Number(eform.low_threshold || 0),
      });
      toast.success(`${edit.name} updated`);
      setEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/stock/${del.id}`);
      toast.success(`${del.name} removed from stock`);
      setDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openRestock = (item) => {
    setRestockFor(item);
    setRestock({ quantity: "", unit_cost: String(item.unit_cost) });
  };

  const submitRestock = async () => {
    try {
      await api.post(`/stock/${restockFor.id}/restock`, {
        quantity: Number(restock.quantity || 0),
        unit_cost: Number(restock.unit_cost || 0),
      });
      toast.success("Delivery recorded, stock increased");
      setRestockFor(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openHistory = async (item) => {
    try {
      const { data } = await api.get(`/stock/${item.id}/ledger`);
      setHistory(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const selectedSku = catalog.find((c) => c.id === form.catalog_sku_id);
  const trackedNames = new Set(items.map((s) => s.name));
  const available = catalog.filter((c) => !trackedNames.has(c.name));

  const paged = usePaged(items);
  const value = items.reduce((a, s) => a + s.quantity * s.unit_cost, 0);
  const low = items.filter((s) => s.quantity <= s.low_threshold);
  const canHqWrite = can(user, "hq:write");
  const hqOnly = !isAdmin && can(user, "hq:read");

  if (hqOnly) {
    return (
      <div>
        <PageHead title="HQ Warehouse" subtitle="Stock position, purchase logging and cleanup" />
        <StockAdminTools defaultStudioId="all" onChanged={load} />
        <StockOverview canWrite={false} canHqWrite={canHqWrite} canClear={user.role === "network_admin"} />
      </div>
    );
  }

  return (
    <div>
      <PageHead title="Stock" subtitle={isAdmin ? "All studios" : user.studio_name}>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-stock-btn">
                <Plus className="mr-2 h-4 w-4" /> Track catalog SKU
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Track a catalog SKU</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500">
                Only SKUs from the HQ-managed global catalog can be stocked. If a SKU is missing,
                contact HQ to add it to the catalog.
              </p>
              {adminNeedsStudio && (
                <p
                  className="rounded bg-amber-50 p-2 text-xs text-amber-800"
                  data-testid="stock-pick-studio-hint"
                >
                  Pick a studio in the filter above first — stock always belongs to one studio.
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label>Catalog SKU *</Label>
                  <Select
                    value={form.catalog_sku_id}
                    onValueChange={(v) => {
                      const sku = catalog.find((c) => c.id === v);
                      setForm((f) => ({
                        ...f,
                        catalog_sku_id: v,
                        unit_cost: sku ? String(sku.hq_sale_price) : f.unit_cost,
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="stock-sku-select">
                      <SelectValue placeholder="Select SKU from catalog" />
                    </SelectTrigger>
                    <SelectContent>
                      {available.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} — {c.category} · {c.unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {available.length === 0 && (
                    <p className="mt-1.5 text-xs text-slate-500" data-testid="stock-no-sku-hint">
                      Every catalog SKU is already tracked — use Restock to add quantity.
                    </p>
                  )}
                </div>
                {isAdmin ? (
                  <>
                    <div>
                      <Label>Quantity ({selectedSku?.unit || "unit"})</Label>
                      <Input
                        data-testid="stock-quantity-input"
                        value={form.quantity}
                        onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Unit cost (₹)</Label>
                      <Input
                        data-testid="stock-unit-cost-input"
                        value={form.unit_cost}
                        onChange={(e) => setForm((f) => ({ ...f, unit_cost: e.target.value }))}
                      />
                    </div>
                  </>
                ) : (
                  <p className="sm:col-span-2 text-xs text-slate-500" data-testid="stock-derived-hint">
                    It starts at zero on hand — quantity is added by HQ deliveries and reduced by
                    job consumption and wastage.
                  </p>
                )}
                <div>
                  <Label>Low threshold</Label>
                  <Input
                    data-testid="stock-threshold-input"
                    value={form.low_threshold}
                    onChange={(e) => setForm((f) => ({ ...f, low_threshold: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-stock-btn" onClick={submit} disabled={!form.catalog_sku_id || adminNeedsStudio}>
                  Save material
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHead>

      {canManageStock && (
        <StockAdminTools defaultStudioId={isAdmin ? studioId : user.studio_id} onChanged={load} />
      )}

      {!isAdmin && (
        <p
          className="mb-6 rounded-md border border-brand-line bg-slate-50 p-4 text-sm text-slate-600"
          data-testid="stock-derived-notice"
        >
          Quantity on hand is calculated from HQ deliveries in, job consumption out and wastage
          out. It cannot be edited here — if a delivery quantity was wrong, ask HQ to correct that
          delivery. Open the ledger on any row to see every movement.
        </p>
      )}

      {low.length > 0 && !isAdmin && (
        <div
          data-testid="low-stock-alert-banner"
          className="mb-6 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.8} />
          <div>
            <p className="font-semibold">Reorder needed for {low.length} SKU(s)</p>
            <p className="mt-0.5">{low.map((s) => s.name).join(", ")}</p>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="mb-6 grid gap-6 sm:grid-cols-3">
          <Kpi label="SKUs" value={items.length} testid="kpi-stock-skus" />
          <Kpi label="Stock value" value={money(value)} testid="kpi-stock-value" />
          <Kpi label="Low stock" value={low.length} testid="kpi-low-stock" accent="text-amber-600" />
        </div>
      )}

      {isAdmin && <StockOverview canWrite={canWrite} canHqWrite={canHqWrite} canClear={user.role === "network_admin"} />}

      <RollStockSummary studioId={isAdmin ? studioId : user.studio_id} />

      {canWrite && !isAdmin && <StudioRolls studioId={user.studio_id} />}

      {isAdmin && (
        <div className="mb-4">
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger className="w-[220px]" data-testid="stock-studio-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="stock-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>Material</TableHead>
                {isAdmin && <TableHead>Studio</TableHead>}
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Unit cost</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-right">Reorder at</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} className="p-0">
                    <EmptyState
                      icon={PackagePlus}
                      testid="stock-empty-state"
                      title="No materials tracked yet"
                      hint={
                        canWrite
                          ? "Track the catalog SKUs you hold, then record deliveries as they arrive."
                          : "Nothing has been stocked here yet."
                      }
                      action={
                        canWrite ? (
                          <Button data-testid="stock-empty-add-btn" onClick={() => setOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" /> Track catalog SKU
                          </Button>
                        ) : null
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {paged.visible.map((s) => (
                <TableRow key={s.id} className="hover:bg-brand-tint/60">
                  <TableCell data-label="Material" className="font-medium">{s.name}</TableCell>
                  {isAdmin && (
                    <TableCell data-label="Studio" className="text-slate-500">
                      {studios.find((x) => x.id === s.studio_id)?.name || "—"}
                    </TableCell>
                  )}
                  <TableCell data-label="Category" className="text-slate-500">{s.category}</TableCell>
                  <TableCell data-label="Quantity" className="text-right">
                    {s.quantity.toFixed(2)} {s.unit}
                    {Number(s.purchase_unit_size || 1) !== 1 && (
                      <span className="block text-xs text-slate-400" data-testid={`stock-equiv-${s.id}`}>
                        {(s.quantity * s.purchase_unit_size).toFixed(0)} {s.consumption_unit} remaining
                      </span>
                    )}
                  </TableCell>
                  <TableCell data-label="Unit cost" className="text-right">{money(s.unit_cost)}</TableCell>
                  <TableCell data-label="Value" className="text-right">{money(s.quantity * s.unit_cost)}</TableCell>
                  <TableCell data-label="Reorder at" className="text-right text-slate-500">
                    {s.low_threshold} {s.unit}
                  </TableCell>
                  <TableCell data-label="" className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {s.quantity <= s.low_threshold && (
                        <Badge
                          data-testid={`low-stock-flag-${s.id}`}
                          className="border-0 bg-amber-50 text-amber-700"
                        >
                          <AlertTriangle className="mr-1 h-3 w-3" /> Low
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`restock-history-btn-${s.id}`}
                        onClick={() => openHistory(s)}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      {canWrite && isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`restock-btn-${s.id}`}
                          onClick={() => openRestock(s)}
                        >
                          <PackagePlus className="mr-1.5 h-3.5 w-3.5" /> Restock
                        </Button>
                      )}
                      {canWrite && isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-stock-${s.id}`}
                          onClick={() => openEdit(s)}
                        >
                          <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                        </Button>
                      )}
                      {canWrite && isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          data-testid={`delete-stock-${s.id}`}
                          onClick={() => setDel(s)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ShowMore paged={paged} testid="stock-show-more" />
        </CardContent>
      </Card>

      <Dialog open={!!restockFor} onOpenChange={(o) => !o && setRestockFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record delivery — {restockFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity received ({restockFor?.unit})</Label>
              <Input
                data-testid="restock-quantity-input"
                value={restock.quantity}
                onChange={(e) => setRestock((r) => ({ ...r, quantity: e.target.value }))}
              />
            </div>
            <div>
              <Label>Cost per unit (₹)</Label>
              <Input
                data-testid="restock-unit-cost-input"
                value={restock.unit_cost}
                onChange={(e) => setRestock((r) => ({ ...r, unit_cost: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="save-restock-btn"
              onClick={submitRestock}
              disabled={!Number(restock.quantity)}
            >
              Record delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-stock-dialog">
          <DialogHeader>
            <DialogTitle>Edit {edit?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            SKU name, category and unit come from the HQ catalog and cannot be changed here.
          </p>
          <div className="space-y-4">
            {[
              ["quantity", `Quantity on hand (${edit?.unit || "unit"})`],
              ["unit_cost", "Unit cost (₹)"],
              ["low_threshold", "Reorder at"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input
                  className="min-h-[44px]"
                  inputMode="decimal"
                  data-testid={`edit-stock-${k}-input`}
                  value={eform[k]}
                  onChange={(e) => setEform((f) => ({ ...f, [k]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button className="min-h-[44px]" data-testid="save-edit-stock-btn" onClick={saveEdit}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-stock-dialog">
          <DialogHeader>
            <DialogTitle>Remove {del?.name} from stock?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This clears the item and its delivery history for this studio. Past job material costs
            are not affected. Use Edit if you only need to correct the quantity.
          </p>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setDel(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-stock-btn"
              onClick={confirmDelete}
            >
              Delete item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl" data-testid="stock-ledger-dialog">
          <DialogHeader>
            <DialogTitle>Stock ledger — {history?.item?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Quantity on hand is derived from these movements only: HQ deliveries in, job
            consumption out and wastage out. It is never typed in.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ["Delivered in", history?.delivered_in, "text-emerald-600"],
              ["Opening", history?.opening_in, "text-sky-600"],
              ["Adjusted", history?.adjusted, "text-amber-600"],
              ["Consumed", history?.consumed_out, "text-brand-ink"],
              ["On hand", history?.derived_balance, "text-brand-ink"],
            ].map(([label, value, tone]) => (
              <div key={label} className="rounded-md border border-brand-line p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                <p className={`font-display text-lg font-semibold ${tone}`}>
                  {Number(value ?? 0).toFixed(2)} {history?.item?.unit}
                </p>
              </div>
            ))}
          </div>
          <Table data-testid="stock-ledger-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Movement</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(history?.movements || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-slate-500">
                    Nothing has moved yet — no HQ delivery, consumption or wastage.
                  </TableCell>
                </TableRow>
              )}
              {(history?.movements || []).map((m, i) => (
                <TableRow key={i}>
                  <TableCell className="whitespace-nowrap">{fmtDate(m.date)}</TableCell>
                  <TableCell className="text-slate-600">
                    {m.type}
                    {m.corrected && (
                      <Badge className="ml-2 border-0 bg-amber-50 text-amber-700">corrected</Badge>
                    )}
                    {m.reference && (
                      <span className="block text-xs text-slate-400">{m.reference}</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={`text-right font-medium tabular-nums ${
                      m.quantity < 0 ? "text-rose-600" : "text-emerald-600"
                    }`}
                  >
                    {m.quantity > 0 ? "+" : ""}
                    {Number(m.quantity).toFixed(3)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {Number(m.balance).toFixed(3)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
